import React, { useState } from 'react';

import { RadioOrSelectOption } from '../../../../../types/common.types';
import {
  HashTag,
  Member,
  MemberProfile,
  Post,
  PostWithHierarchy,
  SearchResult
} from '../../../../../types/entity.types';

import { ARCHIVED, UPCOMING } from '../../../../../utils/constants';

import TribePostsFeed from '../feed/TribePostsFeed';
import TribeMessageCompose from '../message-compose/TribeMessageCompose';
import TribePostHierarchy from '../post/TribePostHierarchy';
import TribeProfile from '../profile/TribeProfile';
import TribeSearchResults from '../search-results/TribeSearchResults';
import TribeSettings from '../settings/TribeSettings';

type Props = {
  status: string;
  settingsOpen: boolean;
  searchResults?: SearchResult[];
  postWithHierarchy?: PostWithHierarchy;
  memberExternalReference?: string;
  profilePicture?: string;
  members: Member[];
  pendingApprovals: number;
  searchType?: RadioOrSelectOption;
  posts: Post[];
  hashtags?: HashTag[];
  profile?: MemberProfile;
  memberPosts: Post[];
  approvePosts: boolean;
  approvePhotos: boolean;
  approveComments: boolean;
  setSearchType: (option: RadioOrSelectOption) => void;
  loadPostWithHierarchy: (externalReference: string) => void;
  loadPosts: () => void;
  refreshPost: (postExternalReference: string) => void;
  setApprovePosts: (value: boolean) => void;
  setApprovePhotos: (value: boolean) => void;
  setApproveComments: (value: boolean) => void;
  createHashtag: () => void;
  updateHashtag: (id: string | number, value: string) => void;
  deleteHashtag: (id: string | number) => void;
  clearPost: () => void;
  clearPostSearchResults: () => void;
  executeSearch: (value: string) => void;
};

const TribeContent = ({
  status,
  settingsOpen,
  searchResults,
  postWithHierarchy,
  memberExternalReference,
  profilePicture,
  members,
  pendingApprovals,
  searchType,
  posts,
  hashtags,
  profile,
  memberPosts,
  approvePosts,
  approvePhotos,
  approveComments,
  setSearchType,
  loadPostWithHierarchy,
  loadPosts,
  refreshPost,
  setApprovePosts,
  setApprovePhotos,
  setApproveComments,
  createHashtag,
  updateHashtag,
  deleteHashtag,
  clearPost,
  clearPostSearchResults,
  executeSearch
}: Props) => {
  const [composing, setComposing] = useState(false);
  const [filterValue, setFilterValue] = useState('');

  const handleComposeClick = () => {
    if (status !== ARCHIVED) {
      setComposing(true);
    }
  };

  const handleBackClick = () => {
    setComposing(false);
    clearPost();
  };

  const handleSearchResultsBackClick = () => {
    clearPostSearchResults();
    setFilterValue('');
  };

  const handleSearchClick = () => executeSearch(filterValue);

  const handleSearchTypeChange = (option: RadioOrSelectOption) => setSearchType(option);

  const handleFilterValueChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => setFilterValue(target.value);

  if (settingsOpen || status === UPCOMING) {
    return (
      <TribeSettings
        status={status}
        approvePosts={approvePosts}
        setApprovePosts={setApprovePosts}
        approvePhotos={approvePhotos}
        setApprovePhotos={setApprovePhotos}
        approveComments={approveComments}
        setApproveComments={setApproveComments}
        hashtags={hashtags}
        onCreateHashtag={createHashtag}
        onUpdateHashtag={updateHashtag}
        onDeleteHashtag={deleteHashtag}
      />
    );
  }

  if (composing) {
    return (
      <TribeMessageCompose
        onBackClick={handleBackClick}
        profilePicture={profilePicture}
        hashtags={hashtags}
        members={members}
        onPostSuccess={() => setComposing(false)}
        searchType={searchType}
        status={status}
        pendingApprovals={pendingApprovals}
      />
    );
  }

  if (postWithHierarchy) {
    return (
      <TribePostHierarchy
        postWithHierarchy={postWithHierarchy}
        profilePicture={profilePicture}
        status={status}
        onComposeClick={handleComposeClick}
        searchType={searchType}
        onSearchTypeChange={handleSearchTypeChange}
        filterValue={filterValue}
        onFilterValueChange={handleFilterValueChange}
        members={members}
        onSearchClick={handleSearchClick}
        onBackClick={handleBackClick}
        pendingApprovals={pendingApprovals}
        hashtags={hashtags}
        refreshPost={refreshPost}
      />
    );
  }

  if (searchResults) {
    return (
      <TribeSearchResults
        results={searchResults}
        profilePicture={profilePicture}
        onBackClick={handleSearchResultsBackClick}
        onComposeClick={handleComposeClick}
        status={status}
        members={members}
        pendingApprovals={pendingApprovals}
        searchType={searchType}
        onSearchTypeChange={handleSearchTypeChange}
        filterValue={filterValue}
        onFilterValueChange={handleFilterValueChange}
        onSearchClick={handleSearchClick}
        onLoadPostWithHierarchy={loadPostWithHierarchy}
      />
    );
  }

  if (memberExternalReference) {
    return (
      <TribeProfile
        profile={profile}
        setComposing={setComposing}
        status={status}
        searchType={searchType}
        onSearchTypeChange={handleSearchTypeChange}
        filterValue={filterValue}
        onFilterValueChange={handleFilterValueChange}
        loadPosts={loadPosts}
        posts={memberPosts}
        refreshPost={refreshPost}
        hashtags={hashtags}
        members={members}
        pendingApprovals={pendingApprovals}
        onSearchClick={handleSearchClick}
      />
    );
  }

  return (
    <TribePostsFeed
      setComposing={setComposing}
      profilePicture={profilePicture}
      status={status}
      onComposeClick={handleComposeClick}
      searchType={searchType}
      onSearchTypeChange={handleSearchTypeChange}
      filterValue={filterValue}
      onFilterValueChange={handleFilterValueChange}
      loadPosts={loadPosts}
      posts={posts}
      refreshPost={refreshPost}
      hashtags={hashtags}
      members={members}
      pendingApprovals={pendingApprovals}
      onSearchClick={handleSearchClick}
    />
  );
};

export default TribeContent;
